import {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useDailyCareLogFilters} from '../../../contexts/dailycare-log-filters/context';
import ActivitiesSectorFilterCollapsible from './activities-sector-filter-collapsible';
import {DailyCareLogSocialResponsesFilters} from './daily-care-log-social-responses-filters';
import {
  AttendanceFilterGroup,
  DailyCareLogFilters,
} from '../../../contexts/dailycare-log-filters/types';
import CheckboxSkeleton from './checkbox-skeleton';
import {translations} from '../../../i18n/translations';
import CollapsibleCheckbox from './collapsible-checkbox';
import {DailyCareLogUsersFilters} from './daily-care-log-users-filters';
import NestedCheckbox from '../../checkbox/nested-checkbox';
import ActionsDrawer, {ActionsDrawerProps} from '../task-table/actions-drawer';
import {useEntity} from '../../../contexts/entity/context';

interface FiltersDrawerProps
  extends Omit<ActionsDrawerProps, 'title' | 'onClearClick' | 'onSaveClick'> {
  activeFilters: DailyCareLogFilters;
  type?: 'tasks' | 'occurrences' | 'attendance-tracker';
  onFiltersSave: (tasksFilters: DailyCareLogFilters) => void;
}

/** Filters of the attendance tracker sessions, and the values of each one */
const ATTENDANCE_FILTER_SECTIONS: Array<{group: AttendanceFilterGroup; values: string[]}> = [
  {group: 'attendanceMovements', values: ['entry', 'exit']},
  {group: 'attendanceStatus', values: ['open', 'closed']},
  {group: 'attendancePeriods', values: ['morning', 'afternoon', 'night']},
];

export const filtersDrawerLoading = () => {
  return new Array(3).fill(1).map((_item, index) => <CheckboxSkeleton key={index} />);
};

export const DailyCareLogFiltersDrawer = ({
  activeFilters,
  onFiltersSave,
  open,
  type = 'tasks',
  onDrawerClose,
  ...rest
}: FiltersDrawerProps) => {
  const [expandedType, setExpandedType] = useState(false);
  const [expandedStatus, setExpandedStatus] = useState(false);
  const [allStatus, setAllStatus] = useState(false);
  const [allTypesGeneral, setAllTypesGeneral] = useState(false);
  const [allTypesPatients, setAllTypesPatients] = useState(false);
  const [expandedAttendance, setExpandedAttendance] = useState<Record<string, boolean>>({});
  const {
    state: {occurrencesTypes},
  } = useEntity();
  const {
    tasksFilters,
    activitiesSectors,
    setTasksFilters: setActiveFilters,
    fetchActivitiesSectors,
    clearAllFilters,
    toggleStatusFilter,
    toggleAllStatusFilters,
    toggleOccurrenceTypeFilter,
    toggleAllOccurrenceTypeFilters,
    toggleAttendanceFilter,
    toggleAllAttendanceFilters,
  } = useDailyCareLogFilters();

  const {entityId}: {entityId: string} = useParams();
  const {t} = useTranslation();
  const generalOccurrences = useMemo(() => {
    return occurrencesTypes?.filter((type) => type.occurrenceType.category === 'general') || [];
  }, [occurrencesTypes]);
  const patientOccurrences = useMemo(() => {
    return occurrencesTypes?.filter((type) => type.occurrenceType.category === 'patients') || [];
  }, [occurrencesTypes]);

  // (Re-)initialize with the current filters
  useEffect(() => {
    if (open && type === 'tasks') {
      setActiveFilters(activeFilters);

      if (!activitiesSectors) {
        fetchActivitiesSectors(entityId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId, activitiesSectors, activeFilters]);

  useEffect(() => {
    if (tasksFilters.status?.length > 0) setAllStatus(true);
    else if (tasksFilters.status?.length === 0) setAllStatus(false);
  }, [tasksFilters.status]);

  useEffect(() => {
    if (tasksFilters.occurrencesTypesGeneral?.length > 0) setAllTypesGeneral(true);
    else if (tasksFilters.occurrencesTypesGeneral?.length === 0) setAllTypesGeneral(false);
  }, [tasksFilters.occurrencesTypesGeneral]);

  useEffect(() => {
    if (tasksFilters.occurrencesTypesPatients?.length > 0) setAllTypesPatients(true);
    else if (tasksFilters.occurrencesTypesPatients?.length === 0) setAllTypesPatients(false);
  }, [tasksFilters.occurrencesTypesPatients]);

  return (
    <ActionsDrawer
      title={t(translations.daily_care_log.task_table.drawer.filters.title)}
      onClearClick={clearAllFilters}
      onSaveClick={() => {
        // Apply the changes made to the filters to filter the tasks on screen
        onFiltersSave(tasksFilters);

        // Close the drawer on save
        onDrawerClose();
      }}
      onDrawerClose={onDrawerClose}
      open={open}
      {...rest}
    >
      {type === 'attendance-tracker' &&
        ATTENDANCE_FILTER_SECTIONS.map(({group, values}) => {
          const texts = translations.daily_care_log.attendance_tracker.filters;
          const selectedValues = tasksFilters[group] ?? [];

          return (
            <CollapsibleCheckbox
              key={group}
              label={t(texts.sections[group])}
              expanded={!!expandedAttendance[group]}
              checked={selectedValues.length === values.length}
              onChange={(checked) => toggleAllAttendanceFilters(group, checked ? values : [])}
              toggleExpanded={(expanded) =>
                setExpandedAttendance((previous) => ({...previous, [group]: expanded}))
              }
            >
              {values.map((value) => (
                <NestedCheckbox
                  key={value}
                  label={t(texts.values[value])}
                  checked={selectedValues.includes(value)}
                  onChange={(selected) => toggleAttendanceFilter(group, value, selected)}
                />
              ))}
            </CollapsibleCheckbox>
          );
        })}

      {type === 'occurrences' && (
        <CollapsibleCheckbox
          label={t(translations.office.users_filters.sections.taskStatus.title)}
          expanded={expandedStatus}
          checked={allStatus}
          onChange={(checked) => {
            toggleAllStatusFilters(
              checked,
              checked ? ['registered', 'not_handled', 'handled'] : []
            );
            setAllStatus(checked);
          }}
          toggleExpanded={setExpandedStatus}
        >
          <NestedCheckbox
            key="registered"
            label={t(translations.office.professional_caregiver.occurrences.status.registered)}
            checked={tasksFilters.status?.includes('registered')}
            onChange={(selected) => toggleStatusFilter('registered', selected)}
          />
          <NestedCheckbox
            key="not_handled"
            label={t(translations.office.professional_caregiver.occurrences.status.not_handled)}
            checked={tasksFilters.status?.includes('not_handled')}
            onChange={(selected) => toggleStatusFilter('not_handled', selected)}
          />
          <NestedCheckbox
            key="handled"
            label={t(translations.office.professional_caregiver.occurrences.status.handled)}
            checked={tasksFilters.status?.includes('handled')}
            onChange={(selected) => toggleStatusFilter('handled', selected)}
          />
        </CollapsibleCheckbox>
      )}

      {type === 'occurrences' && (
        <>
          <CollapsibleCheckbox
            label={t(translations.office.users_filters.sections.patientOccurrences.title)}
            expanded={expandedType}
            checked={allTypesPatients}
            onChange={(checked) => {
              toggleAllOccurrenceTypeFilters(
                checked,
                checked ? patientOccurrences.map((type) => type.occurrenceType.id) : [],
                'patients'
              );
              setAllTypesPatients(checked);
            }}
            toggleExpanded={setExpandedType}
          >
            {patientOccurrences.map((occurrenceType) => {
              const typeChecked = tasksFilters.occurrencesTypesPatients?.includes(
                occurrenceType.occurrenceType.id
              );
              return (
                <NestedCheckbox
                  key={occurrenceType.occurrenceType.id}
                  label={occurrenceType.occurrenceType.name}
                  checked={typeChecked}
                  onChange={(selected) =>
                    toggleOccurrenceTypeFilter(
                      occurrenceType.occurrenceType.id,
                      selected,
                      'patients'
                    )
                  }
                />
              );
            })}
          </CollapsibleCheckbox>
          <CollapsibleCheckbox
            label={t(translations.office.users_filters.sections.generalOccurrences.title)}
            expanded={expandedType}
            checked={allTypesGeneral}
            onChange={(checked) => {
              toggleAllOccurrenceTypeFilters(
                checked,
                checked ? generalOccurrences.map((type) => type.occurrenceType.id) : [],
                'general'
              );
              setAllTypesGeneral(checked);
            }}
            toggleExpanded={setExpandedType}
          >
            {generalOccurrences.map((occurrenceType) => {
              const typeChecked = tasksFilters.occurrencesTypesGeneral?.includes(
                occurrenceType.occurrenceType.id
              );
              return (
                <NestedCheckbox
                  key={occurrenceType.occurrenceType.id}
                  label={occurrenceType.occurrenceType.name}
                  checked={typeChecked}
                  onChange={(selected) =>
                    toggleOccurrenceTypeFilter(
                      occurrenceType.occurrenceType.id,
                      selected,
                      'general'
                    )
                  }
                />
              );
            })}
          </CollapsibleCheckbox>
        </>
      )}

      {type !== 'tasks'
        ? null
        : activitiesSectors
          ? activitiesSectors.map((activitiesSector) => (
              <ActivitiesSectorFilterCollapsible
                key={activitiesSector.id}
                entityId={entityId}
                activitiesSector={activitiesSector}
              />
            ))
          : filtersDrawerLoading()}
      {type !== 'attendance-tracker' && <DailyCareLogSocialResponsesFilters />}

      {type === 'occurrences' && <DailyCareLogUsersFilters />}
    </ActionsDrawer>
  );
};
